import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, ArrowLeft, Loader2, Gift, Tag, Check, X } from "lucide-react";
import { toast } from "sonner";

const Checkout = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const caseId = params.get("case") || "last_toast";
  const [caseInfo, setCaseInfo] = useState(null);
  const [tier, setTier] = useState("base");
  const [playtestPromo, setPlaytestPromo] = useState(null);
  const [form, setForm] = useState({
    host_name: "",
    host_email: "",
    host_phone: "",
    event_date: "",
    group_size: 6,
    event_type: "birthday",
  });
  const [isGift, setIsGift] = useState(false);
  const [gift, setGift] = useState({ recipient_name: "", recipient_email: "", gift_message: "", deliver_at: "" });
  const [age18, setAge18] = useState(false);
  const [loading, setLoading] = useState(false);
  // Shelf 1 — Gift card redemption at checkout
  const [giftCard, setGiftCard] = useState({
    code: (params.get("gift") || "").toUpperCase(),
    recipient_email: "",
    applied: null,       // {discount_cents, cart_total_cents, mode, will_convert_to_stored_value}
    checking: false,
    error: "",
  });
  // Shelf 2 — Party Pack add-ons (shown only when a case has packs)
  const [packsManifest, setPacksManifest] = useState(null);
  const [selectedPacks, setSelectedPacks] = useState([]);

  useEffect(() => {
    api.get(`/packs/for-case/${caseId}`).then((r) => {
      setPacksManifest(r.data);
      // Preselect from ?packs=invitation,decor param (Night Planner wizard)
      const presel = (params.get("packs") || "").split(",").filter(Boolean);
      if (presel.length && r.data?.available) {
        const valid = new Set([...(r.data.packs || []).map(p => p.id), r.data.bundle?.id].filter(Boolean));
        setSelectedPacks(presel.filter((id) => valid.has(id)));
      }
    }).catch(() => setPacksManifest(null));
  }, [caseId, params]);

  useEffect(() => {
    api.get(`/cases/${caseId}`).then((r) => {
      setCaseInfo(r.data);
      // Default group_size to smallest supported by this case
      const sizes = r.data.group_sizes || [6];
      setForm((f) => ({ ...f, group_size: sizes[0] }));
      // Default tier for tiered cases
      if (r.data.tiers) setTier("base");
    }).catch(() => toast.error("Case not found"));
    api.get("/playtest/horror-promo").then((r) => {
      if (r.data?.active && r.data?.applies_to?.includes(caseId) && r.data?.remaining > 0) {
        setPlaytestPromo(r.data);
      }
    }).catch(() => {});
  }, [caseId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const activeTier = caseInfo?.tiers?.[tier];
  const priceLabel = activeTier?.price_label || caseInfo?.price_label || "$35";

  // --- Gift Card redemption (Shelf 1 v1) ---
  const applyGiftCard = async () => {
    if (!giftCard.code || giftCard.code.length < 16) {
      setGiftCard((g) => ({ ...g, error: "Enter the full 16-character code." }));
      return;
    }
    if (!giftCard.recipient_email) {
      setGiftCard((g) => ({ ...g, error: "Add the email the gift was sent to." }));
      return;
    }
    setGiftCard((g) => ({ ...g, checking: true, error: "" }));
    try {
      const { data } = await api.post("/gifts/apply-preview", {
        code: giftCard.code,
        recipient_email: giftCard.recipient_email,
        case_id: caseId,
        buyer_email: form.host_email || undefined,
      });
      setGiftCard((g) => ({ ...g, checking: false, applied: data, error: "" }));
      toast.success(`$${(data.discount_cents / 100).toFixed(2)} applied`);
    } catch (err) {
      setGiftCard((g) => ({
        ...g, checking: false, applied: null,
        error: err?.response?.data?.detail || "Could not apply this code.",
      }));
    }
  };
  const removeGiftCard = () => {
    setGiftCard({ code: "", recipient_email: "", applied: null, checking: false, error: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!age18) {
      toast.error("You must confirm all guests are 18+.");
      return;
    }
    if (isGift) {
      if (!gift.recipient_email || !gift.deliver_at) {
        toast.error("Add the recipient's email and the delivery date.");
        return;
      }
    }
    setLoading(true);
    try {
      const refCode = (localStorage.getItem("wasntme_ref") || "").trim();
      const { data } = await api.post("/payments/checkout", {
        ...form,
        case_id: caseId,
        tier: caseInfo?.tiers ? tier : null,
        group_size: parseInt(form.group_size, 10),
        origin_url: window.location.origin,
        ref_code: refCode,
        is_gift: isGift,
        recipient_name: isGift ? gift.recipient_name : "",
        recipient_email: isGift ? gift.recipient_email : "",
        gift_message: isGift ? gift.gift_message : "",
        deliver_at: isGift ? gift.deliver_at : "",
        gift_card_code: giftCard.applied ? giftCard.code : "",
        pack_ids: selectedPacks,
      });
      if (refCode) {
        api.post("/events", { event: "ref_code_used", ref_code: refCode }).catch(() => {});
      }
      api.post("/events", {
        event: isGift ? "gift_purchased" : "checkout_started",
        props: { case_id: caseId, is_gift: isGift },
      }).catch(() => {});
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen" data-testid="checkout-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] transition-colors mb-10" data-testid="back-link">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">
          {caseInfo ? `case #${caseInfo.number} · ${caseInfo.title}` : "open a new case"}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">Set the scene.</h1>
        <p className="text-[#a39797] mb-10">{caseInfo ? caseInfo.tagline : "One payment. One group. One unforgettable dinner."}</p>

        <form onSubmit={submit} className="card-noir p-8 space-y-6">
          {playtestPromo && (
            <div className="p-4 border border-[#8b0000] bg-[#8b0000]/10 rounded flex items-start gap-3"
                 data-testid="playtest-checkout-banner">
              <span className="text-2xl leading-none">🎃</span>
              <div className="flex-1">
                <div className="text-xs tracking-widest text-[#8b0000] mb-1">HORROR PLAYTESTER · {playtestPromo.remaining}/{playtestPromo.max_redemptions} LEFT</div>
                <div className="text-sm text-[#f4f0ec]">
                  Paste <code className="font-mono text-[#d4af37] bg-black/40 px-2 py-0.5 rounded">{playtestPromo.code}</code> at Stripe checkout
                  for <strong>{playtestPromo.percent_off}% off</strong>. Just drop us your playtest notes in the survey after the game.
                </div>
                <button type="button" onClick={() => { navigator.clipboard.writeText(playtestPromo.code); toast.success(`${playtestPromo.code} copied.`); }}
                        className="btn-ghost text-xs mt-2" data-testid="copy-playtest-code">
                  Copy code
                </button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-2">HOST NAME</label>
            <input className="input-noir" placeholder="Who's throwing this?" value={form.host_name}
              onChange={(e) => update("host_name", e.target.value)} required data-testid="input-host-name" />
          </div>

          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-2">EMAIL (where we send the kit)</label>
            <input type="email" className="input-noir" placeholder="you@example.com" value={form.host_email}
              onChange={(e) => update("host_email", e.target.value)} required data-testid="input-host-email" />
          </div>

          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-2">PHONE (optional — SMS when everyone's in)</label>
            <input type="tel" className="input-noir" placeholder="+14155551234" value={form.host_phone}
              onChange={(e) => update("host_phone", e.target.value)} data-testid="input-host-phone" />
            <div className="text-xs text-[#a39797] mt-1 font-hand">Use E.164 format with +country code. We'll text you once when the ring is full.</div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs tracking-widest text-[#a39797] mb-2">EVENT DATE</label>
              <input type="date" className="input-noir" value={form.event_date}
                onChange={(e) => update("event_date", e.target.value)} required data-testid="input-event-date" />
            </div>
            <div>
              <label className="block text-xs tracking-widest text-[#a39797] mb-2">GROUP SIZE</label>
              <select className="input-noir" value={form.group_size}
                onChange={(e) => update("group_size", e.target.value)} data-testid="select-group-size">
                {(caseInfo?.group_sizes || [6, 7, 8]).map((s) => (
                  <option key={s} value={s}>{s} {s === 1 ? "person" : "people"}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-2">OCCASION</label>
            <select className="input-noir" value={form.event_type}
              onChange={(e) => update("event_type", e.target.value)} data-testid="select-event-type">
              <option value="birthday">Birthday</option>
              <option value="bachelorette">Bachelorette</option>
              <option value="game-night">Game night</option>
              <option value="dinner-party">Dinner party</option>
              <option value="halloween">Halloween</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="pt-4 border-t border-[#2d2424]" data-testid="gift-toggle-section">
            <label className="flex items-start gap-3 text-sm text-[#f4f0ec] cursor-pointer">
              <input type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)}
                className="mt-1 accent-[#d4af37]" data-testid="checkbox-is-gift" />
              <span>
                <span className="flex items-center gap-2">
                  <Gift size={16} className="text-[#d4af37]" />
                  <span className="font-display text-lg">Send as a gift</span>
                </span>
                <span className="text-xs text-[#a39797]">We'll email your recipient on the delivery date with their dashboard link + your message.</span>
              </span>
            </label>
            {isGift && (
              <div className="mt-4 pl-7 space-y-3" data-testid="gift-fields">
                <input className="input-noir" placeholder="Recipient's name" value={gift.recipient_name}
                  onChange={(e) => setGift({ ...gift, recipient_name: e.target.value })}
                  data-testid="input-recipient-name" />
                <input type="email" className="input-noir" placeholder="Recipient's email" value={gift.recipient_email}
                  onChange={(e) => setGift({ ...gift, recipient_email: e.target.value })}
                  required={isGift}
                  data-testid="input-recipient-email" />
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs tracking-widest text-[#a39797] mb-1">DELIVER ON</label>
                    <input type="date" className="input-noir" value={gift.deliver_at}
                      onChange={(e) => setGift({ ...gift, deliver_at: e.target.value })}
                      required={isGift}
                      data-testid="input-deliver-at" />
                  </div>
                </div>
                <textarea className="input-noir min-h-[80px]" placeholder="Gift message (optional)"
                  value={gift.gift_message}
                  onChange={(e) => setGift({ ...gift, gift_message: e.target.value })}
                  data-testid="input-gift-message" />
                <div className="text-xs text-[#a39797] font-hand rotate-[-0.5deg]">
                  You'll get a receipt today. We hold the dashboard email until the date you pick.
                </div>
              </div>
            )}
          </div>

          <label className="flex items-start gap-3 text-sm text-[#a39797] pt-2 cursor-pointer">
            <input type="checkbox" checked={age18} onChange={(e) => setAge18(e.target.checked)}
              className="mt-1 accent-[#8b0000]" data-testid="checkbox-age" required />
            <span>Every guest at this event will be 18 or older. No minors, no corporate team-building.</span>
          </label>

          {caseInfo?.tiers && (
            <div className="pt-4 border-t border-[#2d2424]" data-testid="tier-selector">
              <div className="text-xs tracking-widest text-[#a39797] mb-3">PICK YOUR TIER</div>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(caseInfo.tiers).map(([key, t]) => (
                  <button key={key} type="button" onClick={() => setTier(key)}
                    data-testid={`tier-${key}`}
                    className={`card-noir p-5 text-left transition-colors ${
                      tier === key ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"
                    }`}>
                    <div className="flex items-baseline justify-between">
                      <div className="font-display text-xl">{t.label}</div>
                      <div className="font-display text-lg text-[#d4af37]">{t.price_label}</div>
                    </div>
                    <ul className="mt-3 space-y-1">
                      {t.perks.map((p, i) => (
                        <li key={i} className="text-xs text-[#a39797] leading-relaxed">· {p}</li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Shelf 2 — Party Packs (horror case add-ons) */}
          {packsManifest?.available && (
            <div className="pt-4 border-t border-[#2d2424]" data-testid="party-packs">
              <div className="flex items-center gap-2 mb-2">
                <Skull size={14} className="text-[#d4af37]" />
                <div className="text-xs tracking-widest text-[#a39797]">
                  PRINTABLE PARTY PACKS — add to this order
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {packsManifest.packs.map((p) => {
                  const checked = selectedPacks.includes(p.id);
                  return (
                    <label key={p.id}
                      data-testid={`pack-${p.id}`}
                      className={`card-noir p-3 cursor-pointer transition-colors ${checked ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-display text-base">{p.label}</div>
                          <div className="text-xs text-[#a39797] mt-1">{p.description}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-sm text-[#f4f0ec]">${p.price_cents / 100}</div>
                          <input type="checkbox" checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedPacks((s) => [...s.filter((x) => x !== packsManifest.bundle.id), p.id]);
                              else setSelectedPacks((s) => s.filter((x) => x !== p.id));
                            }}
                            className="accent-[#8b0000]" />
                        </div>
                      </div>
                    </label>
                  );
                })}
                {packsManifest.bundle && (() => {
                  const checked = selectedPacks.includes(packsManifest.bundle.id);
                  const sum = packsManifest.packs.reduce((a, b) => a + b.price_cents, 0);
                  const saves = Math.max(0, sum - packsManifest.bundle.price_cents);
                  return (
                    <label className={`card-noir p-3 cursor-pointer transition-colors ${checked ? "border-[#d4af37] bg-[#d4af37]/10" : "hover:border-[#d4af37]"}`}
                      data-testid={`pack-${packsManifest.bundle.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-display text-base text-[#d4af37]">
                            {packsManifest.bundle.label} <span className="text-xs text-[#a39797]">(save ${(saves / 100).toFixed(0)})</span>
                          </div>
                          <div className="text-xs text-[#a39797] mt-1">
                            All three packs — invitation, costume cards, and decor — bundled.
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-sm text-[#f4f0ec]">${packsManifest.bundle.price_cents / 100}</div>
                          <input type="checkbox" checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedPacks([packsManifest.bundle.id]);
                              else setSelectedPacks((s) => s.filter((x) => x !== packsManifest.bundle.id));
                            }}
                            className="accent-[#d4af37]" />
                        </div>
                      </div>
                    </label>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-[#2d2424]" data-testid="gift-card-redeem">
            <div className="flex items-center gap-2 mb-2">
              <Tag size={14} className="text-[#d4af37]" />
              <div className="text-xs tracking-widest text-[#a39797]">GIFT CARD CODE?</div>
            </div>
            {giftCard.applied ? (
              <div className="card-noir p-4 flex items-center justify-between" data-testid="gift-card-applied">
                <div className="flex items-start gap-3">
                  <Check size={18} className="text-[#4ade80] mt-0.5" />
                  <div>
                    <div className="text-sm text-[#f4f0ec]">
                      ${(giftCard.applied.discount_cents / 100).toFixed(2)} applied
                      <span className="text-[#a39797] text-xs ml-2">
                        ({giftCard.applied.mode === "gift_case" ? "gift for this case" : "gift balance"})
                      </span>
                    </div>
                    {giftCard.applied.will_convert_to_stored_value && (
                      <div className="text-xs text-[#a39797] mt-1">
                        You already own this case — your gift is now a balance you can use anywhere.
                      </div>
                    )}
                  </div>
                </div>
                <button type="button" onClick={removeGiftCard} className="btn-ghost text-xs"
                  data-testid="btn-remove-gift-card">
                  <X size={14} /> remove
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid md:grid-cols-2 gap-2">
                  <input className="input-noir font-mono tracking-wider uppercase"
                    placeholder="XXXX-XXXX-XXXX-XXXX" maxLength={24}
                    value={giftCard.code}
                    onChange={(e) => setGiftCard((g) => ({ ...g, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16), error: "" }))}
                    data-testid="input-gift-card-code" />
                  <input type="email" className="input-noir"
                    placeholder="The email the gift was sent to"
                    value={giftCard.recipient_email}
                    onChange={(e) => setGiftCard((g) => ({ ...g, recipient_email: e.target.value, error: "" }))}
                    data-testid="input-gift-card-email" />
                </div>
                <button type="button" onClick={applyGiftCard} disabled={giftCard.checking}
                  className="btn-ghost text-xs" data-testid="btn-apply-gift-card">
                  {giftCard.checking ? <Loader2 size={14} className="animate-spin" /> : <Tag size={14} />}
                  {giftCard.checking ? "checking…" : "apply gift card"}
                </button>
                {giftCard.error && (
                  <div className="text-xs text-[#ff6b6b]" data-testid="gift-card-error">{giftCard.error}</div>
                )}
                <div className="text-xs text-[#a39797]">
                  Have a wasntme.party gift? Enter the code and the email we sent it to.
                  Need to check a balance? <Link to="/gifts/balance" className="underline">Here.</Link>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-[#2d2424] flex items-center justify-between">
            <div>
              <div className="text-xs text-[#a39797] tracking-widest">TOTAL</div>
              {giftCard.applied ? (
                <>
                  <div className="font-display text-xl text-[#a39797] line-through" data-testid="checkout-subtotal">
                    {priceLabel}.00
                  </div>
                  <div className="font-display text-3xl text-[#f4f0ec]" data-testid="checkout-total">
                    ${((giftCard.applied.cart_total_cents - giftCard.applied.discount_cents) / 100).toFixed(2)}
                  </div>
                </>
              ) : (
                <div className="font-display text-3xl text-[#f4f0ec]" data-testid="checkout-total">{priceLabel}.00</div>
              )}
              <div className="text-xs text-[#a39797] mt-1">Full refund before you lock the kit.</div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50"
              data-testid="submit-checkout">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Skull size={18} />}
              {loading ? "Opening case..." : (giftCard.applied ? "Pay balance & begin" : `Pay ${priceLabel} & begin`)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
